import React, { Fragment, useMemo } from 'react';
import uniqueId from 'lodash.uniqueid';
import { contains, has, head, isEmpty, last, path, pathOr, prop, sum } from 'ramda';
import { useTooltip } from '@vx/tooltip';
import { Group } from '@vx/group';
import { scaleLinear } from '@vx/scale';
import { useIntl } from 'gatsby-plugin-intl';
import { feature as topoFeature } from 'topojson-client';
import { NaturalEarth, Graticule } from '@vx/geo';
import { ParentSize } from '@vx/responsive';

import { renderWhenTrue } from '../../../utils/rendering';
import { TooltipWithBounds } from '../tooltip';
import { NoData, isNoData } from '../noData';
import { Header } from '../header';
import { Container, Svg } from '../charts.styles';
import { DEFAULT_SIZE } from '../charts.constants';
import {
  MapLegend,
  LegendTitle,
  LegendScale,
  LegendBar,
  CrisisBar,
  CrisisLabels,
  NoScore,
  LegendLabels,
  LegendItem,
  Chart,
  TooltipElementTable,
} from './mapChart.styles';
import { CRISIS_DEFAULT, MAP_DEFAULT } from '../configs/map.config';
import {
  GRATICULE_STEP,
  MAP_SCALE_FACTOR,
  MAP_SCALE_HEIGHT_FACTOR,
  MAP_SCALE_WIDTH_FACTOR,
} from '../mapChart/mapChart.constants';
import topology from './countries-50m';
import { TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';
import { toFixed } from '../../../utils/math';
import { TOOLTIP_MIN_WIDTH } from '../../tooltipElementTable/tooltipElementTable.constants';

const world = topoFeature(topology, topology.objects.countries);

const renderLegendItem = (value, key) => <LegendItem key={key}>{value}</LegendItem>;

const renderLegend = ({ legendVisibility, legendStructure, colors }) =>
renderWhenTrue(() => {
    const [topTitle, scores, bottomTitle] = legendStructure;
    const { formatMessage } = useIntl();
    return (
      <MapLegend>
        <LegendTitle>{formatMessage({ id: topTitle })}</LegendTitle>
        <LegendScale>
          <LegendBar colors={colors} />
          <LegendLabels>{scores.map(renderLegendItem)}</LegendLabels>
        </LegendScale>
        <LegendTitle>{formatMessage({ id: bottomTitle })}</LegendTitle>
      </MapLegend>
    );
  })(legendVisibility);

  const renderCrisisLegend = ({ legendVisibility, legendStructure, colors }) =>
    renderWhenTrue(() => {
    const [topTitle, bottomTitle, noScore] = legendStructure;
    const { formatMessage } = useIntl();
    return (
      <MapLegend>
        <LegendScale>
          <CrisisBar colors={colors} />
          <CrisisLabels>
            <LegendTitle>{formatMessage({ id: topTitle })}</LegendTitle>
            <LegendTitle>{formatMessage({ id: bottomTitle })}</LegendTitle>
          </CrisisLabels>
        </LegendScale>
        <LegendScale>
          <NoScore />
          <LegendTitle>{formatMessage({ id: noScore })}</LegendTitle>
        </LegendScale>
      </MapLegend>
    );
  })(legendVisibility);

const renderTooltip = ({
  tooltipOpen,
  tooltipTop,
  tooltipLeft,
  tooltipData: { title, content, score = 0, color } = {},
  formatMessage,
}) =>
  renderWhenTrue(() => (
    <TooltipWithBounds key={uniqueId()} top={tooltipTop} left={tooltipLeft} title={title}>
      <TooltipElementTable style={{ minWidth: 'unset' }}>
        <thead>
          <tr>
            <th>{formatMessage({ id: 'Country' })}</th>
            <th>{formatMessage({ id: 'Score' })}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <TooltipTableDataCell>{formatMessage({ id: title })}</TooltipTableDataCell>
            <TooltipTableDataCell color={color}>
              <span>{toFixed(score, 2)}</span>
            </TooltipTableDataCell>
          </tr>
          {content && (
            <tr>
              <TooltipTableDataCell>{content} </TooltipTableDataCell>
            </tr>
          )}
        </tbody>
      </TooltipElementTable>
    </TooltipWithBounds>
  ))(tooltipOpen);

  const renderCrisisTooltip = ({
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData: { title, content, score = 0, color } = {},
    formatMessage,
  }) => {
  
   return renderWhenTrue(() => (
      <TooltipWithBounds key={uniqueId()} top={tooltipTop} left={tooltipLeft} title={title}>
        <TooltipElementTable style={{ minWidth: 'unset' }}>
          <thead>
            <tr>
              <th>{formatMessage({ id: 'Country' })}</th>
              <th>{formatMessage({ id: '% SBL Change' })}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TooltipTableDataCell>{formatMessage({ id: title })}</TooltipTableDataCell>
              <TooltipTableDataCell color={color}>
                <span>{isNaN(parseInt(score)) ? 'Did not adopt' : toFixed(score, 2) }</span>
              </TooltipTableDataCell>
            </tr>
          </tbody>
        </TooltipElementTable>
      </TooltipWithBounds>
    ))(tooltipOpen);
  }

const handleCountryClick = ({
  name,
  score,
  color,
  item,
  centroid: [tooltipLeft, tooltipTop],
  chartData,
  config,
  showTooltip,
  hideTooltip,
  showChart,
}) => () => {
  if (isEmpty(item)) return hideTooltip();

  showTooltip({
    tooltipLeft: tooltipLeft + TOOLTIP_MIN_WIDTH / 2,
    tooltipTop,
    tooltipData: {color, score, title: name, content: config.tooltipContentRenderer(item, chartData, showChart) },
  });
};

const renderCountry = ({ data, chartData, geo, config, showTooltip, hideTooltip }) => (feature, index) => {
  const { feature: f } = feature;
  if (contains(f.id, config.omittedTerritories)) return null;
  const name = pathOr('', ['properties', 'name'], f);
  let dataCopy = {}
  Object.entries(data).forEach(([key, value]) => {
    dataCopy[key.replace(/\*/,'')] = value;
  })
  const item = dataCopy[name] || {};
  const centroid = path(['features', index, 'centroid'], geo);
  const showChart = !isEmpty(chartData.series);

  const score = useMemo(() => {
    if (isEmpty(chartData.series)) {
      return item.total;
    }

    const mean =
      sum(
        Object.keys(item).map((key) => {
          return chartData.series.includes(key) ? item[key] : 0;
        })
      ) / chartData.series.length;

    return mean;
  }, [item, chartData]);

  return (
    <path
      key={index}
      d={geo.path(f)}
      fill={item.color || config.noDataColor}
      stroke={config.stroke}
      strokeWidth={config.strokeWidth}
      name={name}
      onClick={handleCountryClick({
        color: item.color || config.noDataColor,
        score,
        name: item.locality || name,
        item,
        centroid,
        chartData,
        config,
        showTooltip,
        hideTooltip,
        showChart,
      })}
    />
  );
};

const renderGraticule = ({ geo, config }) =>
  renderWhenTrue(() => (
    <Fragment>
      <Graticule
        outline={(g) => geo.path(g) || ''}
        step={GRATICULE_STEP}
        fill={config.graticuleFill}
        stroke={config.graticuleStroke}
        strokeWidth={config.graticuleStrokeWidth}
      />
      <Graticule
        graticule={(g) => geo.path(g) || ''}
        step={GRATICULE_STEP}
        stroke={config.graticuleStroke}
        strokeWidth={config.graticuleStrokeWidth}
      />
    </Fragment>
  ))(config.graticuleVisible);

export const MapChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  data: chartData = {},
  parser: customParser,
  tabs,
  config: customConfig = {},
} = {}) => {

  if (customParser) customParser(chartData);
  const intl = useIntl();
  chartData['title'] = intl.formatMessage({ id: chartData.title });
  const { title, subtitle, country, data, serie } = chartData;

  if (isNoData(country, data, serie)) return <NoData width={width} height={height} />;

  const config = {
    ...MAP_DEFAULT,
    ...customConfig,
  };

  const crisisBudgetingConfig = {
    ...CRISIS_DEFAULT,
    ...customConfig
  }

  const getCountry = prop(country);
  const getValue = prop(serie);

  const { colors } = config;
  const values = data.map(getValue).filter(value => !isNaN(value));
  const realMin = Math.min(...values);
  const realMax = Math.max(...values);
  const min = has('minValue', config) ? config.minValue : realMin;
  const max = has('maxValue', config) ? config.maxValue : realMax;
  const step = (max - min) / colors.length;


  const colorScale = scaleLinear({
    domain: [realMin, ...colors.map((_, index) => min + index * step), realMax],
    range: [head(colors), ...colors, last(colors)],
  });

  

  const filteredData = data.reduce((list, item) => {
    
    const color = (item.sBLChange && isNaN(item.sBLChange)) ? '#ABABAB' : colorScale(getValue(item) || item.total);
    const country = getCountry(item)

    list[country] = {
      ...item,
      color,
    };

    return list;
  }, {});

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip();

  const { formatMessage } = intl;
  return (
    <Container width={width}>
      <Header title={title} subtitle={subtitle} tabs={tabs} config={config} />
      <Chart width={width} height={height} type={config.type}>
        <ParentSize>
          {({ width: chartWidth, height: chartHeight }) => {
            if (!chartWidth || !chartHeight) return null;
            const xMapCenter = chartWidth / 2;
            const yMapCenter = chartHeight / 2;

            const scale = serie === 'sBLChange' ? Math.min(chartWidth / MAP_SCALE_WIDTH_FACTOR, chartHeight / MAP_SCALE_HEIGHT_FACTOR) * 190 :
              Math.min(chartWidth / MAP_SCALE_WIDTH_FACTOR, chartHeight / MAP_SCALE_HEIGHT_FACTOR) * MAP_SCALE_FACTOR;
            
            const center = serie === 'sBLChange' ? [20, 0] :
              [config.xAngleOffset, config.yAngleOffset]

            return (
              <React.Fragment>
                <Svg>
                  <rect width={chartWidth} height={chartHeight} fill={config.background} onClick={hideTooltip} />
                  <Group>
                    <NaturalEarth
                      data={world.features}
                      scale={scale}
                      translate={[xMapCenter, yMapCenter]}
                      center={center}
                      clipExtent={[
                        [0, 0],
                        [chartWidth, chartHeight],
                      ]}
                    >
                      {(geo) => (
                        <Group>
                          {renderGraticule({ geo, config })}
                          {geo.features.map(
                            renderCountry({
                              data: filteredData,
                              chartData,
                              geo,
                              config,
                              showTooltip,
                              hideTooltip,
                            })
                          )}
                        </Group>
                      )}
                    </NaturalEarth>
                  </Group>
                </Svg>
                {serie === 'sBLChange' ? renderCrisisTooltip({ tooltipOpen, tooltipTop, tooltipLeft, tooltipData, formatMessage }) 
                : renderTooltip({ tooltipOpen, tooltipTop, tooltipLeft, tooltipData, formatMessage })}
              </React.Fragment>
            );
          }}
        </ParentSize>
      </Chart>
      {serie === "sBLChange" ? renderCrisisLegend(crisisBudgetingConfig) : renderLegend(config)}
    </Container>
  );
};
